"use client";

import { mediaChromeLanguageOf } from "@/components/site/media-chrome-lang";

import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { RotateCcwIcon, SkipBackIcon, SkipForwardIcon } from "lucide-react";
import type { MediaController as MediaControllerElement } from "media-chrome";
import {
  MediaControlBar,
  MediaController,
  MediaFullscreenButton,
  MediaGestureReceiver,
  MediaLoadingIndicator,
  MediaMuteButton,
  MediaPlayButton,
  MediaTimeDisplay,
  MediaTimeRange,
} from "media-chrome/react";
import {
  MediaPlaybackRateMenu,
  MediaPlaybackRateMenuButton,
} from "media-chrome/react/menu";
import { setLanguage } from "media-chrome/dist/utils/i18n.js";
import { useLocale, useTranslations } from "next-intl";

import { VideoWatermark } from "@/components/site/video-watermark";
import { useHlsPlayback } from "@/components/site/use-hls-playback";
import { useVideoViewport } from "@/components/site/use-video-viewport";
import { VideoQualityMenu } from "@/components/site/video-quality-menu";
import { clampToTrial, isFinished, resumePosition, shouldReport } from "@/lib/video/playback";

const HINT_SHOW_MS = 6000;
const HINT_LEAVE_MS = 1000;

const PLAYBACK_RATES = [1, 1.25, 1.5, 2];

const PREF_KEY = "video-prefs";

function readPrefs(): { rate?: number } {
  try {
    return JSON.parse(localStorage.getItem(PREF_KEY) ?? "{}");
  } catch {
    return {};
  }
}

function writePrefs(next: Record<string, unknown>) {
  try {
    localStorage.setItem(PREF_KEY, JSON.stringify({ ...readPrefs(), ...next }));
  } catch {
    // 隐私模式下写不进去，记不住就算了，不影响播放
  }
}

function formatClock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const s = String(total % 60).padStart(2, "0");
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);

  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}
import type {
  SiteVideoCourse,
  SiteVideoEpisode,
  SiteVideoWatermark,
} from "@/templates/site/types";

export function VideoPlayer({
  course,
  episode,
  watermark,
  buyHref = "",
  prevHref = "",
  nextHref = "",
}: {
  course: SiteVideoCourse;
  episode: SiteVideoEpisode | null;

  prevHref?: string;
  nextHref?: string;

  watermark: SiteVideoWatermark | null;

  buyHref?: string;
}) {
  const t = useTranslations("site.video");
  const locale = useLocale();

  setLanguage(mediaChromeLanguageOf(locale));
  const [trialEnded, setTrialEnded] = useState(false);

  const [resumedAt, setResumedAt] = useState<number | null>(null);

  const [volumePercent, setVolumePercent] = useState(100);

  const [hintLeaving, setHintLeaving] = useState(false);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rateBoxRef = useRef<HTMLDivElement>(null);

  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isHls = episode?.mimeType === "application/vnd.apple.mpegurl";
  const hls = useHlsPlayback(videoRef, episode?.url ?? "", isHls);
  const frameRef = useRef<MediaControllerElement>(null);
  useVideoViewport(frameRef, episode?.url ? episode.id : undefined);

  const reportedAtRef = useRef(0);
  // 续播只做一次：做完之后用户自己拖的位置不能再被覆盖
  const resumedRef = useRef("");

  const report = useCallback(
    (progress: number, duration: number, finished: boolean, beacon = false) => {

      if (!course.unlocked || !episode) {
        return;
      }

      void fetch("/api/member/content/view", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          module: course.module,
          contentId: course.contentId,
          videoId: episode.id,
          progress: Math.floor(progress),
          duration: Math.floor(duration),
          finished,
        }),

        keepalive: beacon,
      }).catch(() => {
        // 上报失败就丢掉这一次：进度不是关键数据，不值得为它弹错误打断观看
      });
    },
    [course.contentId, course.module, course.unlocked, episode],
  );

  useEffect(() => {
    function flush(beacon: boolean) {
      const element = videoRef.current;
      if (!element || !episode) return;

      report(element.currentTime, element.duration || 0, false, beacon);
    }

    function handlePageHide() {
      flush(true);
    }

    function handleVisibility() {
      if (document.visibilityState === "hidden") flush(true);
    }

    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibility);
      flush(false);
    };
  }, [episode, report]);

  const applyResume = useCallback(() => {
    const element = videoRef.current;

    if (!element || !episode || resumedRef.current === episode.id) {
      return;
    }

    if (!Number.isFinite(element.duration) || element.duration <= 0) {
      return;
    }

    resumedRef.current = episode.id;

    const resumeAt = resumePosition({
      progressSec: episode.progressSec,
      durationSec: element.duration,
      unlocked: episode.unlocked,
    });

    const rate = readPrefs().rate;
    if (rate && PLAYBACK_RATES.includes(rate)) {
      element.playbackRate = rate;
    }

    if (resumeAt !== null) {
      element.currentTime = resumeAt;
      reportedAtRef.current = resumeAt;
      setResumedAt(resumeAt);
    }
  }, [episode]);

  useEffect(() => {
    applyResume();
  }, [applyResume]);

  const scheduleHintExit = useCallback((delay: number) => {
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    hintTimerRef.current = setTimeout(() => setHintLeaving(true), delay);
  }, []);

  useEffect(() => {
    if (resumedAt === null) return;

    setHintLeaving(false);
    scheduleHintExit(HINT_SHOW_MS);

    return () => {
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    };
  }, [resumedAt, scheduleHintExit]);

  function restartFromStart() {
    const element = videoRef.current;
    if (element) element.currentTime = 0;
    setResumedAt(null);
  }

  function handleVolumeInput(event: React.ChangeEvent<HTMLInputElement>) {
    const element = videoRef.current;
    if (!element) return;
    const next = Number(event.target.value) / 100;
    element.volume = next;
    element.muted = next === 0;
  }

  function toggleRateMenu(open: boolean) {
    const menu = rateBoxRef.current?.querySelector("media-playback-rate-menu");
    if (menu instanceof HTMLElement) menu.hidden = !open;
  }

  function handleVolumeChange() {
    const element = videoRef.current;
    if (!element) return;
    setVolumePercent(Math.round((element.muted ? 0 : element.volume) * 100));
  }

  function handleRateChange() {
    const element = videoRef.current;
    if (element) writePrefs({ rate: element.playbackRate });
  }

  function handleTimeUpdate() {
    const element = videoRef.current;
    if (!element || !episode) return;

    const current = element.currentTime;
    const trial = clampToTrial(current, episode.trialSeconds);

    if (trial.blocked) {
      element.pause();
      element.currentTime = trial.seekTo;
      setTrialEnded(true);
      return;
    }

    if (shouldReport(current, reportedAtRef.current)) {
      reportedAtRef.current = current;
      report(current, element.duration || 0, isFinished(current, element.duration || 0));
    }
  }

  function handlePause() {
    const element = videoRef.current;
    if (!element || !episode || element.ended) return;

    reportedAtRef.current = element.currentTime;
    report(
      element.currentTime,
      element.duration || 0,
      isFinished(element.currentTime, element.duration || 0),
    );
  }

  function handleEnded() {
    const element = videoRef.current;
    if (!element || !episode) return;

    report(element.duration || 0, element.duration || 0, true);
  }

  const trialMarkPercent =
    !course.unlocked && episode && episode.trialSeconds > 0 && duration > 0
      ? Math.min(100, (episode.trialSeconds / duration) * 100)
      : null;

  return (
    <div className="video">
      {episode?.url ? (
        <MediaController
          className="video_kuang"
          ref={frameRef}

          style={

            episode.width && episode.height
              ? ({
                  "--video-w": episode.width,
                  "--video-h": episode.height,
                } as CSSProperties)
              : undefined
          }
        >
          <video
            key={episode.id}
            slot="media"
            ref={videoRef}

            suppressHydrationWarning
            poster={episode.cover || undefined}

            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            onContextMenu={(event) => event.preventDefault()}

            onDragStart={(event) => event.preventDefault()}
            onLoadedMetadata={(event) => {
              setDuration(event.currentTarget.duration || 0);
              applyResume();
            }}
            onTimeUpdate={handleTimeUpdate}
            onVolumeChange={handleVolumeChange}
            onPause={handlePause}
            onEnded={handleEnded}
            onRateChange={handleRateChange}
          >
            {/* 用 <source> 而不是 video 的 src，是为了把 type 明写出来：
                签名之后地址尾巴上挂着 `?e=&s=`，浏览器靠扩展名嗅探类型会犯迷糊 */}
            {!isHls && <source
              src={episode.url}
              {...(episode.mimeType ? { type: episode.mimeType } : {})}
            />}
            {t("noVideoTag")}
          </video>

          {/* 点画面播放 / 暂停由它接管：这颗东西知道什么时候该让开（点在控件条上、
              正在拖进度条、双击要全屏），自己按坐标猜控件条高度那版做不到这些 */}
          <MediaGestureReceiver slot="gestures-chrome" />
          <MediaLoadingIndicator slot="centered-chrome" />

          <VideoWatermark data={watermark} />

          <MediaControlBar className="video_tiao">
            <MediaPlayButton />
            {prevHref ? (
              <a className="video_tiao_ji" href={prevHref} aria-label={t("prevEpisode")}>
                <SkipBackIcon />
              </a>
            ) : null}
            {nextHref ? (
              <a className="video_tiao_ji" href={nextHref} aria-label={t("nextEpisode")}>
                <SkipForwardIcon />
              </a>
            ) : null}
            {/* notoggle：不加的话点一下会切成「剩余时间」，左边直接变成负数 */}
            <MediaTimeDisplay showDuration noToggle />
            <MediaTimeRange>
              {/* 试看截止点画在进度条上：原来是播到那儿突然被拉回来，
                  事先看不出边界在哪 */}
              {trialMarkPercent !== null ? (
                <div
                  className="video_shikan_dian"
                  style={{ "--at": `${trialMarkPercent}%` } as CSSProperties}
                />
              ) : null}
            </MediaTimeRange>
            <div className="video_tiao_tools">
              <div
                className="video_tiao_bei_kuang"
                ref={rateBoxRef}
                onMouseEnter={() => toggleRateMenu(true)}
                onMouseLeave={() => toggleRateMenu(false)}
              >
                <MediaPlaybackRateMenuButton />
                {/* 菜单跟按钮同在一个盒子里：它要是放在盒子外，鼠标从按钮移到
                    菜单上的那一刻就触发 mouseleave，菜单当场关掉 */}
                <MediaPlaybackRateMenu
                  className="video_tiao_bei"
                  rates={PLAYBACK_RATES}
                  anchor="auto"
                  hidden
                />
              </div>
              <VideoQualityMenu levels={hls.levels} quality={hls.quality} onSelect={hls.selectQuality} />
              {/* 音量条平时收起来，只留一颗喇叭；鼠标压上去才从上方弹出，
                  这样它不再常年占着控件条的横向空间 */}
              <div className="video_tiao_yin">
                <MediaMuteButton />
                <div className="video_tiao_yin_tan">
                  <span className="video_tiao_yin_shu">{volumePercent}</span>
                  <input
                    className="video_tiao_yin_gan"
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={volumePercent}
                    aria-label={t("volume")}
                    onChange={handleVolumeInput}
                  />
                </div>
              </div>
              <MediaFullscreenButton />
            </div>
          </MediaControlBar>

          {/* 续播了要说一声，并留一条「从头播放」的退路——否则进来不是 0:00
              会让人以为播放器记错了 */}
          {resumedAt !== null ? (
            <div
              className={`video_xubo${hintLeaving ? " video_xubo_zou" : ""}`}
              onMouseEnter={() => {
                if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
                setHintLeaving(false);
              }}
              onMouseLeave={() => scheduleHintExit(HINT_LEAVE_MS)}
              onTransitionEnd={(event) => {
                if (event.propertyName === "opacity" && hintLeaving) {
                  setResumedAt(null);
                }
              }}
            >
              <RotateCcwIcon className="video_xubo_tu" aria-hidden />
              <span>{t("resumedAt", { time: formatClock(resumedAt) })}</span>
              <button type="button" onClick={restartFromStart}>
                {t("playFromStart")}
              </button>
            </div>
          ) : null}

          {/* 试看到点的提示盖在画面上，不放到视频下面：放下面的话，画面还停在
              最后一帧、看着像卡住了，而说明文字在视线之外。盖上去既挡住了继续
              观看的入口，又把「为什么停了、接下来怎么办」放在眼睛正看着的地方 */}
          {hls.failed && !trialEnded ? <div className="video_shikanwan" role="alert"><p>{t("playbackFailed")}</p><button type="button" className="video_shikanwan_qu" onClick={hls.retry}>{t("playbackRetry")}</button></div> : null}
          {trialEnded ? (
            <div className="video_shikanwan">
              <p>{t("trialEnded")}</p>
              {buyHref ? (
                <a href={buyHref} className="video_shikanwan_qu">
                  {t("goBuy")}
                </a>
              ) : null}
            </div>
          ) : null}
        </MediaController>
      ) : (
        <p className="video_kong">
          {course.groups.length === 0 ? t("noEpisodes") : episode?.playbackUnavailable ? t("playbackFailed") : t("lockedHint")}
        </p>
      )}
    </div>
  );
}
